import React from 'react';
import { useNavigate } from 'react-router-dom';
import Search from './Search';
import { Box, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { logAnalyticsEvent } from '../utils/analytics';

/**
 * Shared A-Z library listing page layout used by both /supplements and /conditions landing pages.
 */
function LibraryListing({
  itemKey,
  groupedItems,
  availableLetters,
  routeBase,
  searchLabel,
  heroTitle,
  heroDescription,
  HeroIcon,
  idPrefix,
  analyticsClickEvent,
  analyticsLetterEvent,
}) {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleLetterClick = (event, letter) => {
    event.preventDefault();
    logAnalyticsEvent(analyticsLetterEvent, { letter, surface: routeBase });
    const targetId = `${idPrefix}-letter-${letter}`;
    const target = document.getElementById(targetId);
    if (target) {
      window.history.pushState(null, '', `#${targetId}`);
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ width: '100%', pb: { xs: 5, md: 8 } }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
        p: { xs: 2.25, sm: 3.25 },
        mb: 2.5,
        borderRadius: '1.25rem',
        color: 'text.primary',
        background: `linear-gradient(135deg, ${theme.surfaces.brandTintSm} 0%, ${theme.palette.background.paper} 65%)`,
        border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
        boxShadow: `0 0.75rem 2rem ${alpha(theme.palette.primary.light, 0.08)}`,
      }}>
        <Stack direction="column" spacing={0.75}>
          <Box sx={{ maxWidth: '42rem' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
              <Box sx={{
                width: { xs: '2.25rem', sm: '2.5rem' },
                height: { xs: '2.25rem', sm: '2.5rem' },
                borderRadius: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.light,
                bgcolor: theme.surfaces.brandTintSm,
                border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
                flexShrink: 0,
              }}>
                <HeroIcon sx={{ fontSize: { xs: '1.15rem', sm: '1.25rem' } }} />
              </Box>
              <Typography variant="h3" component="h1" sx={{
                color: 'text.primary',
                fontSize: { xs: '1.8rem', sm: '2.25rem' },
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
              }}>
                {heroTitle}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{
              color: 'text.secondary',
              maxWidth: '38rem',
              fontSize: { xs: '1rem', sm: '1.05rem' },
              lineHeight: 1.7,
            }}>
              {heroDescription}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── Search ───────────────────────────────────────────── */}
      <Box sx={{ mb: { xs: 4, sm: 4.5 } }}>
        <Search
          itemKey={itemKey}
          dataKey={routeBase}
          detailKey="card"
          routeBase={routeBase}
          label={searchLabel}
        />
      </Box>

      {/* ── A-Z Directory ────────────────────────────────────── */}
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: theme.palette.primary.light, fontWeight: 700 }}>
              Browse A-Z
            </Typography>
            <Typography variant="h5" component="h2" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: '-0.01em', fontSize: { xs: '1.35rem', sm: '1.5rem' } }}>
              Find a {routeBase === 'supplements' ? 'supplement' : 'condition'}
            </Typography>
          </Box>
        </Stack>

        {/* Letter jumper */}
        <Paper elevation={0} sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          mb: { xs: 3, sm: 3.5 },
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: '1rem',
          bgcolor: 'background.paper',
          border: `0.0625rem solid ${alpha(theme.palette.divider, 0.8)}`,
          boxShadow: '0 0.25rem 1rem rgba(0,0,0,0.025)',
        }}>
          {availableLetters.map(letter => (
            <Chip
              key={letter}
              label={letter}
              size="small"
              component="a"
              href={`#${idPrefix}-letter-${letter}`}
              clickable
              onClick={(event) => handleLetterClick(event, letter)}
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                color: 'text.secondary',
                bgcolor: theme.surfaces.brandTintSm,
                border: `0.0625rem solid ${theme.surfaces.brandTintMd}`,
                transition: 'all 0.14s ease',
                '&:hover': {
                  color: theme.palette.primary.light,
                  bgcolor: alpha(theme.palette.primary.light, 0.1),
                  borderColor: alpha(theme.palette.primary.light, 0.28),
                },
              }}
            />
          ))}
        </Paper>

        {/* Letter sections */}
        <Stack spacing={{ xs: 3.5, sm: 4 }}>
          {availableLetters.map(letter => (
            <Box key={letter} id={`${idPrefix}-letter-${letter}`}>
              {/* Chapter header */}
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                <Typography sx={{
                  fontSize: { xs: '1.55rem', sm: '1.85rem' },
                  fontWeight: 800,
                  color: theme.palette.primary.light,
                  lineHeight: 1,
                  minWidth: { xs: '1.8rem', sm: '2.1rem' },
                }}>
                  {letter}
                </Typography>
                <Box sx={{ flex: 1, height: '0.0625rem', bgcolor: alpha(theme.palette.divider, 0.9) }} />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  {groupedItems[letter].length} items
                </Typography>
              </Stack>

              {/* Item grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: { xs: 0.875, sm: 1 },
              }}>
                {groupedItems[letter].map(item => (
                  <Box
                    key={item.key}
                    component="a"
                    href={`/${routeBase}/${item.key}`}
                    onClick={(event) => {
                      event.preventDefault();
                      logAnalyticsEvent(analyticsClickEvent, {
                        key: item.key,
                        ...(routeBase === 'supplements'
                          ? { supplement_key: item.key }
                          : { condition_key: item.key }),
                        surface: `${routeBase}_listing`,
                      });
                      navigate(`/${routeBase}/${item.key}`);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: { xs: '2.625rem', sm: '2.75rem' },
                      px: { xs: 1.25, sm: 1.5 },
                      py: 0.875,
                      borderRadius: '0.875rem',
                      color: 'text.primary',
                      textDecoration: 'none',
                      bgcolor: 'background.paper',
                      border: `0.0625rem solid ${alpha(theme.palette.divider, 0.75)}`,
                      boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.018)',
                      transition: 'all 0.14s ease',
                      '&:hover': {
                        bgcolor: theme.surfaces.brandTintSm,
                        borderColor: theme.surfaces.brandTintMd,
                        color: theme.palette.primary.light,
                        transform: 'translateY(-0.0625rem)',
                        boxShadow: `0 0.375rem 1rem ${alpha(theme.palette.primary.light, 0.08)}`,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.005em', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {item.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Container>
  );
}

export default LibraryListing;
